import { CheckCircle2 } from "lucide-react";
import QuoteForm from "./QuoteForm";

/**
 * Reusable service page body block: included services grid, "who / benefits"
 * split, and an embedded QuoteForm.
 *
 * Props:
 *  - included: [{ icon, title, copy }]
 *  - whoFor: [string]
 *  - benefits: [string]
 *  - defaultService: string (preselects value in the form dropdown)
 *  - includedTitle / whoTitle / benefitsTitle: optional heading overrides
 */
export default function ServiceBody({
  included,
  whoFor,
  benefits,
  defaultService,
  includedTitle = "What's included",
  includedSubtitle = "Complete service offering",
}) {
  return (
    <>
      <section className="fss-section bg-white">
        <div className="fss-container">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="fss-overline">{includedTitle}</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              {includedSubtitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {included.map((it, i) => (
              <div key={i} className="fss-card fss-card-hover" data-testid={`included-${i}`}>
                <it.icon className="w-8 h-8 text-green-700" strokeWidth={1.75} />
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">
                  {it.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {(whoFor?.length || benefits?.length) && (
        <section className="bg-slate-50 fss-section border-y border-slate-200">
          <div className="fss-container grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            <div className="bg-white p-10">
              <div className="fss-overline">Who it&apos;s for</div>
              <h3 className="mt-3 font-display text-2xl font-semibold text-slate-900">
                Trusted by teams across Ireland
              </h3>
              <ul className="mt-6 space-y-3">
                {whoFor.map((w, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-green-700 shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-10">
              <div className="fss-overline">Benefits</div>
              <h3 className="mt-3 font-display text-2xl font-semibold text-slate-900">
                Why it matters
              </h3>
              <ul className="mt-6 space-y-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      <section className="fss-section bg-white">
        <div className="fss-container max-w-3xl">
          <div className="text-center mb-10">
            <div className="fss-overline">Request a Quote</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Tell us about your premises
            </h2>
          </div>
          <QuoteForm defaultService={defaultService} />
        </div>
      </section>
    </>
  );
}
